import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { RegionConsensusPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'
import { ExtraSection } from '@/components/feature-extra'

export const Route = createFileRoute('/_site/for/devops')({
  component: DevOpsPage,
  head: () => ({
    meta: [
      { title: 'Infrastructure Uptime Monitoring for DevOps | Uptrack' },
      { name: 'description', content: 'DevOps monitoring: 30s checks, multi-region verification, escalation policies, PagerDuty/OpsGenie webhooks. From $3/mo.' },
      { property: 'og:title', content: 'Infrastructure Uptime Monitoring for DevOps | Uptrack' },
      { property: 'og:description', content: 'Multi-region checks, escalation policies, 30s intervals. Built in Rust with Patroni HA. Free plan, or paid plans from $3/mo.' },
      { property: 'og:image', content: 'https://uptrack.app/og/for-devops.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/devops' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'For DevOps', item: 'https://uptrack.app/for/devops' },
          ],
        }),
      },
    ],
  }),
})

const infraTags = ['EU-hosted', 'NixOS infra-as-code', 'Patroni HA', 'Rust', 'API-first', 'OpenAPI spec']

function DevOpsPage() {
  return (
    <FeaturePage
      section="DevOps monitoring tool"
      eyebrow="For · DevOps monitoring tool"
      title="Monitoring that fits your infrastructure."
      intro="Multi-region checks at 30-second intervals. Escalation policies. Webhook integrations for PagerDuty and OpsGenie. Built in Rust, hosted in Nuremberg, Germany."
      featuresHeading="Built for production infrastructure"
      features={[
        { title: 'Multi-region checks', desc: 'Verify outages from multiple locations before alerting. Avoid false positives from regional network issues.' },
        { title: 'Escalation policies', desc: 'Route alerts through tiers. Notify the on-call first, escalate to the team lead if unacknowledged.' },
        { title: '30-second intervals', desc: 'Detect issues 10x faster than 5-minute checks. Every second matters during an incident.' },
        { title: 'Rust backend', desc: 'The backend is written in Rust and runs on two API nodes. If one node fails, the other keeps serving.' },
        { title: 'Patroni HA database', desc: 'PostgreSQL with automatic failover via Patroni. Your monitoring data survives node failures.' },
        { title: 'Webhook integrations', desc: 'Structured JSON payloads to PagerDuty, OpsGenie, Slack, or any HTTP endpoint you configure.' },
      ]}
      cta={{
        heading: 'Choose a plan for 30-second monitoring.',
        body: 'Full API access and webhook integrations on paid plans, with 30-second checks on every plan.',
      }}
      visual={
        <ProductFrame url="uptrack.app/dashboard/monitors/api">
          <RegionConsensusPiece />
        </ProductFrame>
      }
    >
      <ExtraSection title="Infrastructure you can trust">
        <p className="text-muted-foreground">
          Uptrack runs on NixOS with infrastructure-as-code. Every server configuration is reproducible and
          version-controlled. The database uses Patroni for automatic leader election and failover. Hosted in Nuremberg,
          Germany, with GDPR-compliant data handling.
        </p>
        <p className="mt-4 text-sm text-muted-foreground">{infraTags.join(' · ')}</p>
      </ExtraSection>

      <ExtraSection title="When your on-call page matters">
        <p className="text-muted-foreground">
          Your API starts returning 503s. Several locations confirm the outage before you are paged. Then Uptrack fires
          a webhook to PagerDuty and the on-call engineer gets paged. If no acknowledgment in 5 minutes, the team lead
          is notified. Meanwhile, your status page updates automatically.
        </p>
      </ExtraSection>
    </FeaturePage>
  )
}
